<template>
    <div class="preparation-print-recap-page">
        <transition name="loading-screen">
            <LoadingScreen v-if="loading" />
        </transition>
        <template v-if="errors">
            <ErrorsZone :errors="errors" />
        </template>
        <template v-if="loadedOnce && !errors && order">
            <div class="row mb-4 align-items-center">
                <div class="col-md-6 col-12">
                    <PreparationCustomerOrderCustomerInformation
                        :order="order"
                    />
                </div>
                <div class="col-md-6 col-12">
                    <OrderPreparationProgressBar
                        v-if="order.preparable"
                        :label="order.status_label"
                        :preparation-status="progressCounters"
                    />
                </div>
            </div>

            <div class="row mb-4">
                <div class="col-md-6 col-12 mx-auto alert alert-light border-secondary">
                    <h3>
                        Récapitulatif de commande
                    </h3>

                    <p>
                        <i class="fad fa-exclamation-triangle"></i>
                        <strong>Le récapitulatif de commande</strong> ne peut pas être utilisé pour la livraison d'une commande
                        et <strong>ne peut pas se substituer au bon de livraison</strong>, y compris pour les lieux de retrait.<br />
                        Toutefois, dans des cas très spécifiques, il peut être utile d'avoir le récapitulatif de commande.
                    </p>

                    <div class="row mb-4">
                        <div class="col-12">
                            <a class="btn btn-primary btn-block"
                                :href="`/espace-employes/commandes/preparation/commande/${order.id}/bon-livraison/menu`">
                                <i class="fad fa-print fa-2x"></i>
                                <br>
                                Imprimer plutôt<br>le bon de livraison
                            </a>
                        </div>
                    </div>

                    <div v-if="!displayRecap" class="small">
                        <div>J'ai compris que le récapitulatif de commande :</div>
                        <div class="mt-1">
                            <div v-for="(label, i) in randomConfirm" :key="label" class="form-check">
                                <input type="checkbox" v-model="recapConfirmValues[label]" :value="true" :id="`checkRecapInput${i}`" />
                                <label class="form-check-label" :for="`checkRecapInput${i}`">
                                    {{ label }}
                                </label>
                            </div>
                        </div>
                        <div class="mt-2">
                            <a class="btn btn-outline-secondary btn-sm"
                                :href="`/espace-employes/commandes/preparation/commande/${order.id}/bon-livraison/menu`">
                                Imprimer plutôt le bon de livraison
                            </a>
                            <button class="btn btn-link text-muted btn-sm" v-on:click="checkConfirmValues()">Accéder au récapitulatif quand même</button>
                        </div>
                    </div>

                    <template v-else>
                        <div class="row mb-2">
                            <div class="col-12">
                                <a class="btn btn-outline-secondary btn-block" target="_blank" :href="`/espace-employes/commandes/preparation/commande/${order.id}/recapitulatif`">
                                    <i class="fad fa-file-pdf fa-2x"></i>
                                    <br>
                                    Afficher<br>le récapitulatif de commande
                                </a>
                            </div>
                        </div>
                        <div class="row mb-2">
                            <div v-for="printer in printers" class="col-md-6 col-12 mb-2">
                                <a class="btn btn-secondary btn-block"
                                @click="confirmPrint($event, 'Récapitulatif de commande', printer.name)"
                                :href="`/espace-employes/commandes/preparation/commande/${order.id}/recapitulatif/imprimer/${printer.id}`">
                                    <i class="fad fa-print fa-2x"></i>
                                    <br>
                                    Imprimer le récapitulatif
                                    <br>
                                    {{ printer.name }}
                                </a>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

        </template>
    </div>
</template>

<script>
    import ErrorsZone from "../ErrorsZone";
    import LoadingScreen from "../LoadingScreen";
    import OrderPreparationMenu from "./OrderPreparationMenu";
    import OrderPreparationProgressBar from "./CustomerOrder/OrderPreparationProgressBar";
    import PreparationCustomerOrderCustomerInformation from "./PreparationCustomerOrderCustomerInformation";
    import {useGetLoader} from "../compositions/axios/loadingRequest";

    export default {
        name: 'PreparationPrintRecapPage',

        components: {
            ErrorsZone,
            LoadingScreen,
            OrderPreparationMenu,
            OrderPreparationProgressBar,
            PreparationCustomerOrderCustomerInformation,
        },

        props: {
            orderId: {
                type: String,
                required: true,
            },
            printers: {
                type: Array,
                required: true,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            const confirmData = {
                "Ne peut pas être utilisé pour la livraison d'une commande": true,
                "Ne peut pas se substituer au bon de livraison": true,
                "Peut être utilisé pour livrer une commande": false,
                "Peut être utilisé en remplacement du bon de livraison": false,
                "Peut être utilisé pour lister les produits commandés": true,
                "Peut être utilisé pour lister les produits préparés": false,
                "Peut être utilisé pour la distribution des commandes": false,
                "Ne peut pas être utilisé pour distributer les commandes": true,
            };
            const randomConfirm = _.take(_.shuffle(_.keys(confirmData)), 4);
            let recapConfirm = {};
            let recapConfirmValues = {};
            for (let label of randomConfirm) {
                recapConfirm[label] = confirmData[label];
                recapConfirmValues[label] = false;
            }

            return {
                loaded,
                loading,
                errors,
                load,
                randomConfirm,
                recapConfirm,
                recapConfirmValues,
            };
        },

        data() {
            return {
                order: null,
                progressCounters: {
                    nb_ordered: 0,
                    nb_prepared: 0,
                    nb_missing: 0,
                },
                loadedOnce: false,
                displayRecap: false,
                displayConfirm: false,
            }
        },

        created() {
            Echo.private(`employees`)
                .listen('OrderUpdatedEvent', (e) => {
                    if (e.order.id == this.orderId) {
                        this.loadStateFromServer();
                    }
                });
        },

        async mounted() {
            this.loadStateFromServer();
        },

        methods: {
            async loadStateFromServer() {
                const data = await this.load(`/espace-employes/commandes/preparation/commande/${this.orderId}/json`);
                if (data) {
                    this.order = data.order;
                    this.progressCounters = data.progressCounters;

                    this.loadedOnce = true;
                }
            },

            confirmPrint(event, documentName, printerName) {
                let message = 'Êtes vous sûr de vouloir lancer l\'impression du document '+documentName+' sur l\'imprimante '+printerName+' ?';
                if(!confirm(message)) {
                    event.preventDefault();
                }
            },

            checkConfirmValues() {
                for (let key in this.recapConfirm) {
                    if(this.recapConfirmValues[key] != this.recapConfirm[key]) {
                        alert("Vous n'avez pas bien compris les cas d'usage du récapitulatif de commande.\nVeuillez vérifier vos réponses.");
                        return;
                    }
                }
                this.displayRecap = true;
            },
        },
    }
</script>
